import type React from 'react';
import { Navigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { InstitutionFuturesProgressStep } from '@/context/progress';
import { RouteConfigProvider } from '@/context/route-config';
import FuturesClientCategorisationTreatment from '@/features/futures/components/client-categorisation-treatment';
import FuturesClientCategorisationLayout from '@/features/futures/layouts/client-categorisation';
import { useDetailsNext } from '@/features/institution/hooks/useDetailsNext';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import { useProfileInstitutionDetailsAnswers, useProfileInstitutionDetailsMeta } from '@/hooks/queries/use-profile-institution-details';

const InstitutionFuturesClientCategorisationTreatmentLayout = (props: React.ComponentPropsWithoutRef<typeof FuturesClientCategorisationLayout>) => {
  return <FuturesClientCategorisationLayout progress={0.5} step={InstitutionFuturesProgressStep.CLIENT_CATEGORISATION} {...props} />;
};
InstitutionFuturesClientCategorisationTreatmentLayout.displayName = 'InstitutionFuturesClientCategorisationTreatmentLayout';

const InstitutionFuturesClientCategorisationTreatmentLoading = () => {
  return (
    <InstitutionFuturesClientCategorisationTreatmentLayout>
      <LoadingCard />
    </InstitutionFuturesClientCategorisationTreatmentLayout>
  );
};
InstitutionFuturesClientCategorisationTreatmentLoading.displayName = 'InstitutionFuturesClientCategorisationTreatmentLoading';

const InstitutionFuturesClientCategorisationTreatmentContent = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useProfileInstitutionDetailsAnswers();
  const {
    isFetching: isFetchingInstitutionDetailsMeta,
    isLoading: isLoadingInstitutionDetailsMeta,
    data: { clientCategorisation, isRegulated },
  } = useProfileInstitutionDetailsMeta();
  const { isLoading: isLoadingInstitutionDetailsNext, data: institutionDetailsNext } = useDetailsNext(INSTITUTION_DETAILS_TYPE.FUTURES);
  const isLoading = isLoadingInstitutionDetailsAnswers || isFetchingInstitutionDetailsMeta || isLoadingInstitutionDetailsMeta || isLoadingInstitutionDetailsNext;
  const countryOfIncorporation = institutionDetailsAnswers?.incorporation?.countryOfIncorporation;

  if (isLoading) return <InstitutionFuturesClientCategorisationTreatmentLoading />;

  if (institutionDetailsNext?.route) return <Navigate to={institutionDetailsNext.route} replace />;

  if (!countryOfIncorporation) throw new Error('Unable to proceed with client categorisation, no country of incorporation');

  if (!clientCategorisation) throw new Error('Unable to proceed with client categorisation, no client categorisation');

  return (
    <InstitutionFuturesClientCategorisationTreatmentLayout>
      <FuturesClientCategorisationTreatment
        clientCategorisation={clientCategorisation}
        countryOfResidency={countryOfIncorporation}
        reprocessFuturesEligibility={isRegulated ?? undefined}
      />
    </InstitutionFuturesClientCategorisationTreatmentLayout>
  );
};
InstitutionFuturesClientCategorisationTreatmentContent.displayName = 'InstitutionFuturesClientCategorisationTreatmentContent';

function InstitutionFuturesClientCategorisationTreatmentPage() {
  return (
    <RouteConfigProvider
      config={{
        appropriatenessTest: '/institution/futures/appropriateness-test',
        jurisdictionUnavailable: '/institution/futures/client-categorisation/jurisdiction-unavailable',
        professionalAgreement: '/institution/futures/client-categorisation/professional-agreement',
      }}
    >
      <InstitutionFuturesClientCategorisationTreatmentContent />
    </RouteConfigProvider>
  );
}
InstitutionFuturesClientCategorisationTreatmentPage.displayName = 'InstitutionFuturesClientCategorisationTreatmentPage';

export default InstitutionFuturesClientCategorisationTreatmentPage;
